import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactDialog = ({ open, onOpenChange, productId = null, productName = null }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: productName ? `${t('contact.interested')} ${productName}` : ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        product_id: productId
      };

      await axios.post(`${API}/contact`, payload);
      toast.success(t('contact.success'));
      
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: ''
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(t('contact.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">{t('contact.title')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="font-body text-sm uppercase tracking-wide">{t('contact.name')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="border-b border-t-0 border-l-0 border-r-0 rounded-none px-0 focus:border-foreground"
              data-testid="contact-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="font-body text-sm uppercase tracking-wide">{t('contact.phone')} *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="border-b border-t-0 border-l-0 border-r-0 rounded-none px-0 focus:border-foreground"
              data-testid="contact-phone-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="font-body text-sm uppercase tracking-wide">{t('contact.email')}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="border-b border-t-0 border-l-0 border-r-0 rounded-none px-0 focus:border-foreground"
              data-testid="contact-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="font-body text-sm uppercase tracking-wide">{t('contact.message')} *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={4}
              className="border-b border-t-0 border-l-0 border-r-0 rounded-none px-0 focus:border-foreground"
              data-testid="contact-message-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
            data-testid="contact-submit-button"
          >
            {loading ? t('contact.sending') : t('contact.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
